import { localized, msg, str } from '@lit/localize';
import { css, html, LitElement, nothing } from 'lit';
import { customElement, property } from 'lit/decorators.js';
import type { WorkflowSummary } from '../../../api/types';
import { sharedUiStyles } from '../../../styles/shared-ui.styles';
import '@spectrum-web-components/action-button/sp-action-button.js';
import '@spectrum-web-components/progress-circle/sp-progress-circle.js';
import '@spectrum-web-components/underlay/sp-underlay.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-add.js';
import '@spectrum-web-components/icons-workflow/icons/sp-icon-import.js';

@customElement('kv-template-chooser')
@localized()
export class KvTemplateChooser extends LitElement {
  static styles = [
    sharedUiStyles,
    css`
    :host {
      position: absolute;
      inset: 0;
      display: flex;
      flex-direction: column;
      box-sizing: border-box;
      overflow: auto;
      padding: 28px;
      background:
        linear-gradient(
          180deg,
          var(--color-sidebar-bg, #f8fafc) 0%,
          var(--color-app-bg, #f8fafc) 100%
        );
      z-index: 2;
      color: var(--color-app-text, #1f1f1f);
      --template-surface: var(--kv-surface);
      --template-surface-muted: var(--kv-surface-muted);
      --template-surface-hover: var(--kv-surface-hover);
      --template-border: var(--kv-border);
      --template-accent: var(--kv-accent);
      --template-accent-bg: var(--kv-accent-bg);
      --template-shadow: var(--kv-shadow);
    }
    :host-context(sp-theme[color='dark']) {
      --template-shadow: none;
    }
    .chooser-shell {
      display: flex;
      flex-direction: column;
      gap: 16px;
      margin: 0 auto;
      max-width: 1280px;
      min-width: 0;
      width: min(100%, 1280px);
    }
    .chooser-header {
      align-items: center;
      display: flex;
      gap: 16px;
      justify-content: space-between;
    }
    .header-copy {
      min-width: 0;
    }
    .section-title {
      color: var(--color-app-text);
      font-size: 24px;
      font-weight: 750;
      line-height: 1.15;
      margin: 0;
    }
    .section-description {
      color: var(--color-app-text-secondary);
      font-size: 13px;
      line-height: 1.45;
      margin: 6px 0 0;
      max-width: 720px;
    }
    .back-button {
      --mod-actionbutton-border-color-default: var(--kv-accent-border);
      --mod-actionbutton-border-color-hover: var(--color-accent-border-strong);
      --mod-actionbutton-background-color-default: var(--template-accent-bg);
      --mod-actionbutton-background-color-hover: var(--template-accent-bg);
      --mod-actionbutton-content-color-default: var(--template-accent);
      --mod-actionbutton-content-color-hover: var(--template-accent);
      flex: 0 0 auto;
      font-size: 12px;
      font-weight: 700;
    }

    .applying-underlay {
      position: absolute;
      inset: 0;
      z-index: 3;
    }
    .applying-spinner {
      position: absolute;
      top: 50%;
      left: 50%;
      transform: translate(-50%, -50%);
      z-index: 4;
    }
    .template-surface {
      background: var(--template-surface);
      border: 1px solid var(--template-border);
      border-radius: var(--kv-radius-panel);
      box-shadow: var(--template-shadow);
      display: flex;
      flex-direction: column;
      min-width: 0;
      overflow: hidden;
    }
    .surface-toolbar {
      align-items: center;
      border-bottom: 1px solid var(--template-border);
      display: flex;
      gap: 12px;
      justify-content: space-between;
      padding: 14px 16px;
    }
    .surface-title {
      color: var(--color-app-text);
      font-size: 14px;
      font-weight: 700;
      margin: 0;
    }
    .surface-subtitle {
      color: var(--color-app-text-secondary);
      font-size: 12px;
      margin: 4px 0 0;
    }
    .template-grid {
      display: grid;
      gap: 12px;
      grid-template-columns: repeat(auto-fill, minmax(260px, 1fr));
      padding: 16px;
    }
    .template-card {
      border: 1px solid var(--template-border);
      border-radius: 8px;
      background: var(--template-surface);
      box-sizing: border-box;
      color: inherit;
      cursor: pointer;
      display: flex;
      gap: 12px;
      min-height: 116px;
      min-width: 0;
      padding: 14px;
      text-align: left;
      transition:
        background-color 120ms ease,
        border-color 120ms ease,
        box-shadow 120ms ease,
        transform 120ms ease;
    }
    .template-card--blank {
      background: var(--template-accent-bg);
      border-color: var(--kv-accent-border);
    }
    .template-card:hover {
      background: var(--template-surface-hover);
      border-color: var(--kv-accent-border);
      box-shadow: 0 8px 18px rgba(15, 23, 42, 0.08);
      transform: translateY(-1px);
    }
    .template-card:focus-visible {
      outline: 2px solid var(--template-accent);
      outline-offset: 2px;
    }
    .template-card:disabled {
      opacity: 0.6;
      cursor: not-allowed;
      box-shadow: none;
      transform: none;
    }
    .template-icon {
      align-items: center;
      background: var(--template-accent-bg);
      border: 1px solid var(--kv-accent-border-subtle);
      border-radius: 8px;
      color: var(--template-accent);
      display: inline-flex;
      flex: 0 0 auto;
      height: 36px;
      justify-content: center;
      width: 36px;
    }
    .template-card--blank .template-icon {
      background: var(--template-surface);
    }
    .template-copy {
      min-width: 0;
    }
    .template-card-title {
      color: var(--color-app-text);
      display: -webkit-box;
      font-size: 14px;
      font-weight: 700;
      line-height: 1.25;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 2;
    }
    .template-card-description {
      color: var(--color-app-text-secondary);
      display: -webkit-box;
      font-size: 12px;
      line-height: 1.35;
      margin: 6px 0 0;
      overflow: hidden;
      -webkit-box-orient: vertical;
      -webkit-line-clamp: 3;
    }
    .template-empty,
    .template-loading {
      align-items: center;
      color: var(--color-app-text-secondary);
      display: flex;
      gap: 12px;
      justify-content: center;
      min-height: 180px;
      padding: 24px;
      text-align: center;
    }
    .error {
      align-items: center;
      background: var(--color-error-bg);
      border: 1px solid rgba(220, 38, 38, 0.18);
      border-radius: 8px;
      color: var(--color-error-text);
      display: flex;
      gap: 8px;
      margin: 16px;
      padding: 12px 14px;
    }

    @media (max-width: 768px) {
      :host {
        padding: 16px;
      }
      .chooser-header,
      .surface-toolbar {
        align-items: stretch;
        flex-direction: column;
      }
      .section-title {
        padding-left: var(--kv-mobile-title-inline-start);
      }
      .back-button {
        align-self: flex-start;
      }
      .template-grid {
        grid-template-columns: 1fr;
      }
    }
    `,
  ];

  @property({ attribute: false })
  accessor templates: WorkflowSummary[] = [];

  @property({ type: Boolean })
  accessor loading = false;

  @property({ type: String })
  accessor error: string | null = null;

  @property({ type: Boolean })
  accessor applying = false;

  private startBlank = () => {
    this.dispatchEvent(
      new CustomEvent('start-blank', { bubbles: true, composed: true })
    );
  };

  private triggerImport = () => {
    const input = this.renderRoot.querySelector<HTMLInputElement>(
      '#template-workflow-import-input'
    );
    input?.click();
  };

  private handleImportChange(event: Event) {
    const target = event.target as HTMLInputElement;
    const file = target.files?.[0];
    if (!file) return;

    this.dispatchEvent(
      new CustomEvent('import-workflow', {
        detail: { file },
        bubbles: true,
        composed: true,
      })
    );
    target.value = '';
  }

  private backToList = () => {
    this.dispatchEvent(
      new CustomEvent('back-to-list', { bubbles: true, composed: true })
    );
  };

  private applyTemplate(template: WorkflowSummary) {
    this.dispatchEvent(
      new CustomEvent('apply-template', {
        detail: { template },
        bubbles: true,
        composed: true,
      })
    );
  }

  private get templateCountLabel() {
    if (this.loading) {
      return msg('Loading templates...');
    }
    const count = this.templates.length;
    return count === 1 ? msg('1 template') : msg(str`${count} templates`);
  }

  render() {
    return html`
      ${
        this.applying
          ? html`<sp-underlay class="applying-underlay" open></sp-underlay>
              <sp-progress-circle
                class="applying-spinner"
                label=${msg('Creating automation')}
                indeterminate
              ></sp-progress-circle>`
          : nothing
      }
      <div class="chooser-shell">
        <div class="chooser-header">
          <div class="header-copy">
            <h2 class="section-title">${msg('Start a new automation')}</h2>
            <p class="section-description">
              ${msg('Pick a template to jumpstart your automation, or begin from scratch.')}
            </p>
          </div>
          <sp-action-button class="back-button" @click=${this.backToList}>
            ${msg('Back to automation list')}
          </sp-action-button>
        </div>

        <section class="template-surface" aria-label=${msg('Automation templates')}>
          <div class="surface-toolbar">
            <div>
              <h3 class="surface-title">${msg('Choose a starting point')}</h3>
              <p class="surface-subtitle">${this.templateCountLabel}</p>
            </div>
          </div>
          ${
            this.loading
              ? html`<div class="template-loading">
                  <sp-progress-circle
                    label=${msg('Loading templates')}
                    size="s"
                    indeterminate
                  ></sp-progress-circle>
                  ${msg('Loading templates...')}
                </div>`
              : this.error
                ? html`<div class="error">${this.error}</div>`
                : nothing
          }
          ${
            !this.loading && !this.error
              ? html`
                <div class="template-grid">
                  <input
                    id="template-workflow-import-input"
                    type="file"
                    accept="application/json"
                    hidden
                    @change=${this.handleImportChange}
                  />
                  <button
                    class="template-card template-card--blank"
                    ?disabled=${this.applying}
                    @click=${this.startBlank}
                  >
                    <span class="template-icon">
                      <sp-icon-add size="s"></sp-icon-add>
                    </span>
                    <span class="template-copy">
                      <span class="template-card-title">
                        ${msg('Start blank automation')}
                      </span>
                      <p class="template-card-description">
                        ${msg('Begin with an empty canvas and build from scratch.')}
                      </p>
                    </span>
                  </button>
                  <button
                    class="template-card"
                    ?disabled=${this.applying}
                    @click=${this.triggerImport}
                  >
                    <span class="template-icon">
                      <sp-icon-import size="s"></sp-icon-import>
                    </span>
                    <span class="template-copy">
                      <span class="template-card-title">
                        ${msg('Import a workflow')}
                      </span>
                      <p class="template-card-description">
                        ${msg('Upload a workflow JSON file and continue from it.')}
                      </p>
                    </span>
                  </button>
                  ${this.templates.map((template) => {
                    const label =
                      typeof template.name === 'string' &&
                      template.name.trim().length
                        ? template.name.trim()
                        : msg(str`Automation ${template.id}`);
                    const description =
                      template.description?.trim() ||
                      msg('Use this template as a starting point.');
                    return html`
                      <button
                        class="template-card"
                        ?disabled=${this.applying}
                        @click=${() => this.applyTemplate(template)}
                      >
                        <span class="template-copy">
                          <span class="template-card-title" title=${label}>
                            ${label}
                          </span>
                          <p class="template-card-description" title=${description}>
                            ${description}
                          </p>
                        </span>
                      </button>
                    `;
                  })}
                </div>
              `
              : nothing
          }
          ${
            !this.loading && !this.error && this.templates.length === 0
              ? html`<div class="template-empty">
                  ${msg('No templates available yet.')}
                </div>`
              : nothing
          }
        </section>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'kv-template-chooser': KvTemplateChooser;
  }
}
